// [목적] 축제 댓글 어댑터 팩토리 + 훅 — festival-comments.client 4개 호출을 댓글 대상 어댑터 계약에 매핑
// [이유] festival-comments Spec D-8 — 축제 상세(/festivals/:id) 댓글이 폴리오 댓글과 같은 CommentSection/useComments 를 공유한다.
//         폴리오 어댑터(useFolioComments.ts)와 대칭 구조로 두어 대상별 차이가 이 파일 한 곳에 모이게 한다.
// [사이드 이팩트] 없음(순수 매핑). 쿼리 키는 queryKeys.festivalComments.list(contentId) — 축제 목록/상세 캐시와 분리.
// [수정 시 고려사항] commentIntent 는 'festival.comment'(LoginPromptModal 카피 동기), reportTargetType 은 'festivalComment'.
//                   create 는 입력 객체를 addComment(contentId, input) 로 그대로 통과(TR-9 관례 동일).
//                   축제는 답글 입력 UI 가 없다(Spec OQ-1) — parentId 는 타입상 전달 가능하나 UI 는 사용하지 않음.

import type { FestivalComment } from '@gangwoncanvas/types';

import * as festivalCommentsClient from '../api/festival-comments.client.js';
import { queryKeys } from '../api/queryKeys.js';

import { useComments, type CommentTargetAdapter, type UseCommentsResult } from './useComments.js';

export type UseFestivalCommentsResult = UseCommentsResult<FestivalComment>;

/**
 * [목적] 축제 댓글 어댑터 생성
 * [이유] FestivalCommentSection(래퍼)·useFestivalComments 가 같은 매핑을 공유.
 * [사이드 이팩트] 없음 — 네트워크 호출은 어댑터 메서드 실행 시점.
 * [수정 시 고려사항] contentId 는 TourAPI 숫자 문자열. 경로 인코딩은 client 가 담당.
 */
export function festivalCommentAdapter(contentId: string): CommentTargetAdapter<FestivalComment> {
  return {
    queryKey: queryKeys.festivalComments.list(contentId),
    commentIntent: 'festival.comment',
    reportTargetType: 'festivalComment',
    list: ({ cursor }) => festivalCommentsClient.getComments(contentId, { cursor }),
    create: (input) => festivalCommentsClient.addComment(contentId, input),
    patch: (commentId, text) => festivalCommentsClient.patchComment(contentId, commentId, { text }),
    remove: (commentId) => festivalCommentsClient.deleteComment(contentId, commentId),
  };
}

/**
 * [목적] 축제 댓글 훅 — 목록 무한스크롤 + 작성/수정/삭제 mutation
 * [이유] useFolioComments 와 대칭인 편의 훅(현재 CommentSection 은 어댑터를 직접 받으므로 테스트/재사용용).
 * [사이드 이팩트] mutation 성공 시 축제 댓글 목록만 invalidate.
 * [수정 시 고려사항] contentId 는 부모가 축제 로딩 성공 후에만 마운트한다고 가정.
 */
export function useFestivalComments(contentId: string): UseFestivalCommentsResult {
  return useComments(festivalCommentAdapter(contentId));
}
